import { useMemo, useState } from 'react';

import {
  AlertTriangleIcon,
  ArrowRightIcon,
  InfoIcon,
  SearchIcon,
  SearchXIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  FAQ,
  OPERATOR_FLOW,
  TUTORIALS,
} from '../../components/pages/backoffice/help-center/_data.js';
import Heading from '../../components/typography/heading.jsx';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion.jsx';
import { Badge } from '../../components/ui/badge.jsx';
import { Button } from '../../components/ui/button.jsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card.jsx';
import { DebouncedInput } from '../../components/ui/input-debounced.jsx';

/**
 * Normalizes text so the search ignores case and accents.
 *
 * @param {string} value
 * @return {string}
 */
const normalize = (value) =>
  value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Renders one of the callouts shown at the end of a tutorial.
 *
 * @param {Object} props
 * @param {'info' | 'warning'} props.type
 * @param {string} props.text
 *
 * @return {JSX.Element}
 */
const TutorialNote = ({ type, text }) => {
  const isWarning = type === 'warning';
  const Icon = isWarning ? AlertTriangleIcon : InfoIcon;

  return (
    <div
      className={`flex flex-row items-start gap-3 rounded-md border p-3 ${
        isWarning
          ? 'border-red-500 bg-red-50 dark:bg-red-950/20'
          : 'border-muted-foreground/30 bg-muted'
      }`}
    >
      <Icon
        className={`mt-0.5 size-5 shrink-0 ${
          isWarning ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
        }`}
      />
      <p
        className={`text-sm ${
          isWarning ? 'text-red-700 dark:text-red-300' : 'text-muted-foreground'
        }`}
      >
        {text}
      </p>
    </div>
  );
};

export const HelpCenter = () => {
  const [search, setSearch] = useState('');

  const normalizedSearch = normalize(search.trim());

  const filteredTutorials = useMemo(() => {
    if (!normalizedSearch) return TUTORIALS;

    return TUTORIALS.filter((tutorial) =>
      normalize(
        [
          tutorial.title,
          tutorial.description,
          ...tutorial.tags,
          ...tutorial.steps,
        ].join(' '),
      ).includes(normalizedSearch),
    );
  }, [normalizedSearch]);

  const filteredFaq = useMemo(() => {
    if (!normalizedSearch) return FAQ;

    return FAQ.filter((item) =>
      normalize(`${item.question} ${item.answer}`).includes(normalizedSearch),
    );
  }, [normalizedSearch]);

  const hasResults = filteredTutorials.length > 0 || filteredFaq.length > 0;

  return (
    <div className="flex flex-col pb-10">
      <div className="pb-6">
        <Heading className="pb-2">Centro de Ajuda</Heading>
        <p className="text-muted-foreground">
          Tutoriais passo a passo para as tarefas do dia a dia na plataforma.
        </p>
      </div>

      {/* OPERATOR FLOW */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Como funciona uma participação</CardTitle>
          <CardDescription>
            Do registo do cliente até à atribuição dos cupões.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {OPERATOR_FLOW.map((item, index) => (
              <li
                key={item.title}
                className="flex flex-col gap-2 rounded-lg border bg-muted/40 p-4"
              >
                <div className="flex flex-row items-center gap-2">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                    {index + 1}
                  </span>
                  <span className="font-medium">{item.title}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {item.description}
                </p>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {/* SEARCH */}
      <div className="relative mb-6 max-w-xl">
        <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <DebouncedInput
          className="pl-9"
          debounce={300}
          value={search}
          onChange={(value) => setSearch(String(value))}
          placeholder="Pesquisar por tema, por exemplo: fatura, consentimento, cupões"
        />
      </div>

      {!hasResults ? (
        <div className="flex w-full flex-col items-center justify-center px-6 py-12 text-center">
          <SearchXIcon className="size-16 text-muted-foreground" />
          <h3 className="pt-6 text-xl font-medium">
            Não encontrámos nada sobre &quot;{search.trim()}&quot;
          </h3>
          <p className="max-w-prose pt-2 text-sm text-muted-foreground">
            Experimente outras palavras ou fale com o supervisor.
          </p>
        </div>
      ) : (
        <>
          {filteredTutorials.length ? (
            <section className="pb-8">
              <h2 className="pb-3 text-xl font-medium">Tutoriais</h2>

              <Accordion type="single" collapsible className="w-full">
                {filteredTutorials.map((tutorial) => {
                  const Icon = tutorial.icon;

                  return (
                    <AccordionItem key={tutorial.id} value={tutorial.id}>
                      <AccordionTrigger className="text-left hover:no-underline">
                        <div className="flex flex-row items-center gap-3 pr-4">
                          <Icon className="size-5 shrink-0 text-muted-foreground" />
                          <div className="flex flex-col gap-1">
                            <span className="text-base font-medium">
                              {tutorial.title}
                            </span>
                            <span className="text-sm font-normal text-muted-foreground">
                              {tutorial.description}
                            </span>
                          </div>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent>
                        <div className="flex flex-col gap-5 pb-2 pl-8">
                          <div className="flex flex-row flex-wrap gap-2">
                            {tutorial.tags.map((tag) => (
                              <Badge key={tag} variant="secondary">
                                {tag}
                              </Badge>
                            ))}
                          </div>

                          <ol className="flex list-none flex-col gap-3">
                            {tutorial.steps.map((step, index) => (
                              <li
                                key={index}
                                className="flex flex-row items-start gap-3"
                              >
                                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold">
                                  {index + 1}
                                </span>
                                <span className="text-sm leading-relaxed">
                                  {step}
                                </span>
                              </li>
                            ))}
                          </ol>

                          {tutorial.notes?.length ? (
                            <div className="flex flex-col gap-2">
                              {tutorial.notes.map((note, index) => (
                                <TutorialNote
                                  key={index}
                                  type={note.type}
                                  text={note.text}
                                />
                              ))}
                            </div>
                          ) : null}

                          {tutorial.link ? (
                            <div>
                              <Button asChild variant="outline">
                                <Link to={tutorial.link.to}>
                                  {tutorial.link.label}
                                  <ArrowRightIcon />
                                </Link>
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </section>
          ) : null}

          {filteredFaq.length ? (
            <section>
              <h2 className="pb-3 text-xl font-medium">Problemas Frequentes</h2>

              <Accordion type="single" collapsible className="w-full">
                {filteredFaq.map((item) => (
                  <AccordionItem key={item.id} value={item.id}>
                    <AccordionTrigger className="text-left text-base font-medium">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="pb-2 text-sm leading-relaxed text-muted-foreground">
                        {item.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
};
